import {
  // Axis,
  Color3,
  Mesh,
  MeshBuilder,
  PointLight,
  Scene,
  StandardMaterial,
  Texture,
  Vector3,
  VolumetricLightScatteringPostProcess,
} from '@babylonjs/core';

export enum Fansea3DStageLightType {
  Point,
  Strip,
  Environment,
  // Area
}

export interface Fansea3DStageLightSetting {
  name: string;
  type: Fansea3DStageLightType;
  diffuse?: { r: number; g: number; b: number }; // environment light / environment light takes the light color from the environment
  specular?: { r: number; g: number; b: number }; // light color in shiny materials / environment light takes the light color from the environment
  position?: { x: number; y: number; z: number }; // directional light does not have a position
  direction?: { x: number; y: number; z: number }; // point light does not have a direction
  size?: { width: number; height: number }; // area and strip lights have a size
  density?: number; // we can only simulate area lights with many point lights. density defines the number of lights along one axis
  intensity: number;
  scale?: number;
}

export interface Fansea3DStageLightSetup {
  name: string;
  lights: Fansea3DStageLightSetting[];
}

export const removeAllLights = (scene: Scene) => {
  // remove environment light
  scene.environmentIntensity = 0;

  // Remove all lights
  while (scene.lights.length > 0) {
    scene.lights[0].dispose();
  }

  // Remove all meshes with names containing "light"
  for (let i = 0; i < scene.meshes.length; i++) {
    if (scene.meshes[i].name.includes('light')) {
      scene.meshes[i].dispose();
      i--; // Decrement the index to account for the removed mesh
    }
  }

  if (scene && scene.activeCamera) {
    const lights = scene?.postProcesses.filter(
      (process) => process.name === 'lights'
    );
    lights.forEach((light) => light?.dispose(scene.activeCamera!));
  }
};

// const beamMaxAlpha = 0.2;

const addLightMesh = (
  setting: Fansea3DStageLightSetting,
  scene: Scene,
  intensityScale: number,
  lightScale: number,
  lightPositions: Vector3[]
) => {
  const { intensity, diffuse, scale = 1 } = setting;

  var lightMaterial = new StandardMaterial('glow', scene);
  lightMaterial.diffuseColor = new Color3(
    diffuse?.r ?? 0,
    diffuse?.g ?? 0,
    diffuse?.b ?? 0
  );
  lightMaterial.specularColor = new Color3(0, 0, 0);
  lightMaterial.emissiveColor = diffuse
    ? new Color3(
        diffuse!.r * intensityScale ?? 0,
        diffuse!.g * intensityScale ?? 0,
        diffuse!.b * intensityScale ?? 0
      )
    : new Color3(0, 0, 0);
  // lightMaterial.alpha = beamMaxAlpha;

  let lightMeshes: Mesh[] = [];
  lightPositions.forEach((lightPosition) => {
    var lightSphere = MeshBuilder.CreateSphere(
      'light sphere',
      {
        diameter: 0.5 * lightScale * scale,
        segments: 24,
      },
      scene
    );
    lightSphere.position = lightPosition;
    console.log(lightSphere.position);
    lightSphere.material = lightMaterial;

    lightMeshes.push(lightSphere);
  });

  const mergedMeshes = Mesh.MergeMeshes(lightMeshes)!;

  var lights = new VolumetricLightScatteringPostProcess(
    'lights',
    0.8,
    scene.activeCamera,
    mergedMeshes,
    100,
    Texture.BILINEAR_SAMPLINGMODE,
    scene.getEngine(),
    false
  );

  lights.useDiffuseColor = true;
  lights.exposure = intensity * intensityScale * 0.01;
  lights.decay = 0.96815;
  lights.weight = 0.98767;
  lights.density = 0.996;
};

export const addPointLight = (
  setting: Fansea3DStageLightSetting,
  scene: Scene,
  intensityScale: number,
  worldScale: number,
  visibleLights: boolean,
  lightScale: number = 1
) => {
  const { position, intensity, diffuse, specular } = setting;
  const lightPosition = new Vector3(
    position?.x ?? 0,
    position?.y ?? 0,
    position?.z ?? 0
  ).scale(worldScale);
  const light = new PointLight(setting.name, lightPosition, scene);
  light.intensity = intensity * intensityScale;
  light.diffuse = new Color3(diffuse?.r ?? 0, diffuse?.g ?? 0, diffuse?.b ?? 0);
  light.specular = new Color3(
    specular?.r ?? 0,
    specular?.g ?? 0,
    specular?.b ?? 0
  );

  visibleLights &&
    addLightMesh(setting, scene, intensityScale, lightScale, [lightPosition]);
};

export const addStripLight = (
  setting: Fansea3DStageLightSetting,
  scene: Scene,
  intensityScale: number,
  worldScale: number,
  visibleLights: boolean,
  lightScale: number = 1
) => {
  const { position, intensity, density = 2, diffuse, specular } = setting;

  var lightPositions: Vector3[] = [];

  for (var i = 0; i < density; i++) {
    const start = new Vector3(
      ((-(setting.size?.width ?? 1) / (density - 1)) * density) / 2,
      0,
      ((-(setting.size?.height ?? 0) / (density - 1)) * density) / 2
    );
    const offset = new Vector3(
      start.x + ((setting.size?.width ?? 1) / (density - 1)) * i,
      0,
      start.z + ((setting.size?.height ?? 0) / (density - 1)) * i
    );
    const lightPosition = new Vector3(
      (position?.x ?? 0) + offset.x,
      position?.y ?? 0,
      (position?.z ?? 0) + offset.z
    ).scale(worldScale);
    const light = new PointLight(setting.name, lightPosition, scene);
    light.intensity = intensity * intensityScale;
    light.diffuse = new Color3(
      diffuse?.r ?? 0,
      diffuse?.g ?? 0,
      diffuse?.b ?? 0
    );
    light.specular = new Color3(
      specular?.r ?? 0,
      specular?.g ?? 0,
      specular?.b ?? 0
    );

    lightPositions.push(lightPosition);
  }

  visibleLights &&
    addLightMesh(setting, scene, intensityScale, lightScale, lightPositions);
};

export const addEnvironmentLight = (
  setting: Fansea3DStageLightSetting,
  scene: Scene,
  intensityScale: number
) => {
  const { intensity } = setting;
  scene.environmentIntensity = intensity * intensityScale;
};

export const addLightSetup = (
  setup: Fansea3DStageLightSetup,
  scene: Scene,
  lightIntensityScale: number,
  visibleLights: boolean,
  lightScale: number = 1
) => {
  removeAllLights(scene);

  setup.lights.forEach((setting) => {
    if (setting.type === Fansea3DStageLightType.Point) {
      addPointLight(
        setting,
        scene,
        lightIntensityScale,
        2,
        visibleLights,
        lightScale
      );
    } else if (setting.type === Fansea3DStageLightType.Strip) {
      addStripLight(
        setting,
        scene,
        lightIntensityScale,
        2,
        visibleLights,
        lightScale
      );
    } else if (setting.type === Fansea3DStageLightType.Environment) {
      addEnvironmentLight(setting, scene, lightIntensityScale);
    }
  });
};
